<template>
    <teleport to="#order-details-right-col-vue-container">
        <div id="order-details-right-col">
            <!-- Alert after a successfully adjusted order -->
            <div v-if="adjustmentStatus === 'adjusted'"
                 class="alert alert-success mb-1">
                <i class="fa-solid fa-check"></i> {{ l('ORDER_DETAILS.ADJUSTMENT_SUCCESSFUL') }}
            </div>

            <!-- Alert after a successful balance payment -->
            <div v-if="adjustmentStatus === 'balance-paid'"
                 class="alert alert-success mb-1">
                <i class="fa-solid fa-check"></i> {{ l('ORDER_DETAILS.BALANCE_PAYMENT_SUCCESSFUL') }}
            </div>

            <!-- Alert for voided order -->
            <div v-if="currentOrder.isVoided()"
                 class="alert alert-danger mb-1">
                <i class="fa-solid fa-ban"></i>
                {{ l('ORDER_DETAILS.VOIDED_ORDER_ALERT') }}
            </div>
            <!-- Alert for cancelled orders -->
            <div v-else-if="currentOrder.isCancelled()"
                 class="alert alert-danger mb-1">
                <i class="fa-solid fa-ban"></i>
                {{ l('ORDER_DETAILS.CANCELLED_ORDER_ALERT') }}
            </div>
            <!-- Alert for unconfirmed orders -->
            <div v-else-if="!currentOrder.isConfirmed()"
                 class="alert alert-danger mb-1 pre-wrap">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ unconfirmedOrderAlertText }}
                <div v-if="!currentOrder.isCancelled()"
                     class="mt-05">
                    <ConfirmOrderButton :order="currentOrder"
                                        class="mr-05" />
                    <VoidOrderButton :order="currentOrder" />
                </div>
            </div>

            <!-- Alert for no items in the order -->
            <div v-if="!currentOrder.hasItems()"
                 class="alert alert-transparent mb-1">
                <i class="fa-solid fa-info-circle"></i>
                {{ l('ORDER_DETAILS.NO_ITEMS_ALERT') }}
            </div>

            <!-- Order waivers (if any) -->
            <OrderWaivers v-if="currentOrderWaiverStatus?.existingWaivers?.length > 0"
                          :waiver-status="currentOrderWaiverStatus"
                          :waivers="currentOrderWaiverStatus.existingWaivers"
                          :loading="loadingWaivers"
                          @refresh="loadWaivers" />

            <Tabs :model-value="currentTab"
                  :tabs="orderTabs"
                  @update:model-value="tabSelected" />

            <!-- Order details content based on current tab selection (new) -->
            <OrderDetailsTabContent v-if="selectedTab"
                                    :tab="selectedTab"
                                    :current-order="currentOrder" />
        </div>
    </teleport>
</template>

<script>
import _ from 'lodash';
import ConfirmOrderButton from '@/epos/vue/pages/order-details/components/ConfirmOrderButton';
import EventNames from '@/lib/Events/EventNames';
import OrderDetailsTabContent from '@/epos/vue/pages/order-details/components/OrderDetailsTabContent';
import OrderWaivers from '@/epos/vue/pages/order-details/components/OrderWaivers';
import Tabs from '@/vue/components/Tabs/Tabs';
import VoidOrderButton from '@/epos/vue/pages/order-details/components/VoidOrderButton';
import { container } from '@/container';
import { getLangStringKeyItemTypeVariantsForOrder } from '@/lib/Orders/orderContents';
import { langFilter } from '@/vue/filters/langFilter';
import { mapGetters, mapState } from 'vuex';
import { updateAngularUi } from '@/functions/angular';

/**
 * @typedef {import('@/lib/Orders/orderItemGroups').OrderItemTab} OrderItemTab
 */

export default {
    components: {
        ConfirmOrderButton,
        OrderDetailsTabContent,
        OrderWaivers,
        Tabs,
        VoidOrderButton,
    },

    data() {
        return {
            loadingWaivers: false,
        };
    },

    computed: {
        // orderDetails/currentOrder is set by the Angular OrderDetailsCtrl.
        ...mapState('orderDetails', [
            'currentTab',
            'currentOrder',
            'currentOrderWaiverStatus',
        ]),
        ...mapState(['isOnline']),

        // itemTabs are computed once in the orderDetails Vuex store from currentOrder, and shared
        // with the Angular controller, so both sides always resolve the same IDs.
        ...mapGetters('orderDetails', ['itemTabs']),

        /**
         * The full tab (itemType + all its groups) matching currentTab, for
         * OrderDetailsTabContent to render. currentGroup/currentTab themselves stay as the
         * single-group state Angular and the footer bar rely on (see the watcher below) -
         * this just resolves which whole tab that corresponds to for display purposes.
         *
         * @return {OrderItemTab|null}
         */
        selectedTab() {
            return this.itemTabs.find((tab) => tab.itemType === this.currentTab) || null;
        },

        /**
         * itemTabs, mapped into the {name, label, icon} shape the generic Tabs component expects.
         *
         * Todo: the Tabs component also accepts a badge prop so we could do something nice like pass in item qtys.
         *
         * @return {{ name: string, label: string, icon: string }[]}
         */
        orderTabs() {
            return this.itemTabs.map((tab) => ({
                name: tab.itemType,
                label: tab.nameLangKey,
                icon: tab.icon,
            }));
        },

        /**
         * Get the adjustment status from the query string.
         *
         * @returns {string}
         */
        adjustmentStatus() {
            return container.$location.search()['adjustment-status'];
        },

        /**
         * The text for the unconfirmed order alert.
         * The first line is the alert itself, followed by a line explaining the implications of the order being
         * unconfirmed based on the item types in the order.
         * Returns the formatted and translated text as a single string.
         *
         * @returns {string}
         */
        unconfirmedOrderAlertText() {
            if (!this.currentOrder) {
                return '';
            }

            const itemTypeText = getLangStringKeyItemTypeVariantsForOrder(
                'ORDER_DETAILS.UNCONFIRMED_ORDER_ALERT',
                this.currentOrder
            )
                .map((key) => langFilter(key))
                .join(' ');

            return [langFilter('ORDER_DETAILS.UNCONFIRMED_ORDER_ALERT'), itemTypeText]
                .filter((text) => !!text)
                .join('\n');
        },
    },

    watch: {
        /**
         * @param {Order?} order
         */
        currentOrder: {
            handler(order) {
                if (!order) {
                    this.$store.commit('orderDetails/setCurrentOrderWaiverStatus', []);
                    return;
                }

                this.loadWaivers();
            },
            immediate: true,
        },

        isOnline: {
            handler(isOnline) {
                if (isOnline) {
                    // Reload waivers when coming back online.
                    // This is primarily for if the order was loaded while the device was offline, because that can
                    // cause the UI to be in an inconsistent state and show waiver tickets as redeemable.
                    // But it's a good idea to reload the waivers when coming back online anyway.
                    this.loadWaivers();
                }
            },
        },
    },

    created() {
        const debouncedReloadWaivers = _.debounce(
            () => {
                if (this.currentOrderWaiverStatus) {
                    this.loadWaivers();
                }
            },
            100
        );
        // Listen for redemption activity and reload waivers if needed.
        this.deregisterRedemptionListenerFunc = container.$rootScope.$on(
            EventNames.TICKET_REDEMPTION_ACTIVITY,
            () => debouncedReloadWaivers()
        );
    },

    beforeUnmount() {
        if (this.deregisterRedemptionListenerFunc) {
            this.deregisterRedemptionListenerFunc();
        }

        if (this.registerRedeemRequestListener) {
            this.unregisterRedeemRequestListener();
        }
    },

    methods: {
        loadWaivers() {
            if (!container.userService.company.waiversEnabled()) {
                return;
            }

            this.loadingWaivers = true;
            container.waiversService
                .getOrderWaiverStatus(this.currentOrder.ID)
                .then((orderWaiverStatus) => {
                    this.$store.commit(
                        'orderDetails/setCurrentOrderWaiverStatus',
                        orderWaiverStatus
                    );

                    // Angular uses the currentOrderHasIncompleteWaivers getter from Vuex to
                    // enable/disable the redeem buttons.
                    updateAngularUi();
                })
                .finally(() => {
                    this.loadingWaivers = false;
                });
        },

        /**
         * A tab can contain multiple groups (e.g. Tickets: several sessions plus open tickets) -
         * OrderDetailsTabContent renders all of them based on the selectedTab / currentTab.
         *
         * We still need to broadcast the tab's first group here so $scope.currentGroup which is still used in a few
         * places e.g. the membership-renewal footer bar stays in sync.
         *
         * @see ORDER_DETAILS_TAB_CHANGED in OrderDetailCtrl.
         *
         * @param {string} tabName itemType of the tab selected, from Tabs' v-model.
         */
        tabSelected(tabName) {
            const tab = this.itemTabs.find((itemTab) => itemTab.itemType === tabName);

            if (!tab) {
                return;
            }

            const firstGroup = tab.groups[0];

            this.$store.commit('orderDetails/setCurrentTab', tab.itemType);

            container.eventBus.broadcast(
                EventNames.ORDER_DETAILS_TAB_CHANGED,
                firstGroup,
            );
            updateAngularUi();
        },
    },
};
</script>

<style scoped lang="less">
@import "../../../../../less/variables";

@media (max-width: @mobile-max) {
    .alert-transparent {
        color: @dt-navy;
    }
}
</style>
